// Plan madde 10.2 — operator action → role mapping table.
//
// Frontend RBAC for the operator console. Spec 60 defines 10 operator roles
// (`role_op_*`) with capability bullet-points; this file translates those
// capabilities into action IDs that hooks (usePermission) and guards
// (RequireRole) can reference. The map is INTENTIONALLY DENORMALIZED
// (action → roles[]) rather than (role → actions[]): the consumer
// almost always asks "can the current operator do X?" and a Set lookup
// is O(1) on the answer side.
//
// The map is the FRONTEND policy layer — backend enforcement still owns
// the truth (every /ops/v1/* route checks the operator's role at the
// requireOperatorAuth + requireOperatorRole(role) middleware per plan
// madde 3.10). A frontend permission check is a UX optimization (hide
// buttons the operator can't click anyway, prevent navigation to a
// route that would 403); it is NOT a security boundary. NEVER rely on
// frontend RBAC alone — always pair with backend route-level checks.
//
// Action ID convention: "<domain>.<verb>"
//   tenants.* — cross-tenant directory + detail surface
//   billing.* — financial operations
//   incident.* — SRE workflow
//   flag.*    — feature-flag administration
//   audit.*   — operator audit sink access
//   iam.*     — internal user / role management
//   impersonate — cloud-only support workbench (spec 60 §impersonation)
//
// New actions land here as plan madde 3 ships /ops/v1/* endpoints — the
// route handler's requireOperatorRole(role) call defines the canonical
// allowed-roles list, and this map mirrors it for frontend gating.

/**
 * The 11 operator role IDs from spec 60 §roles. Imported by usePermission
 * + RequireRole. Frozen literal so a typo in a callsite (e.g.,
 * "role_op_finace") fails at compile time.
 */
export const OPERATOR_ROLES = [
  "role_op_staff",
  "role_op_customer_success",
  "role_op_support",
  "role_op_finance",
  "role_op_sre",
  "role_op_sales",
  "role_op_exec",
  "role_op_revenue",
  "role_op_admin",
  "role_op_auditor",
  "role_op_payroll_ops",
] as const;

export type OperatorRole = (typeof OPERATOR_ROLES)[number];

/**
 * Frontend action → allowed roles mapping. The map is the SOURCE OF
 * TRUTH for frontend gating; the backend route's requireOperatorRole()
 * call must agree. When they drift, the backend wins (frontend permission
 * check passes → user clicks the button → backend 403 → showErrorToast
 * surfaces operator.permission_denied) — drift is degraded UX, not a
 * security failure.
 *
 * Currently scoped to actions that have a backend route shipped (madde 3
 * delivery wave) or planned for the next wave. New actions append here.
 */
export const OPERATOR_ACTIONS = {
  // ─── tenants surface (plan madde 3.2) ───────────────────────────
  // Read-only directory + detail. role_op_auditor explicitly EXCLUDED:
  // the auditor role reads the operator audit sink, not customer data.
  "tenants.read": [
    "role_op_staff",
    "role_op_customer_success",
    "role_op_support",
    "role_op_finance",
    "role_op_sre",
    "role_op_sales",
    "role_op_exec",
    "role_op_admin",
  ],

  // Firma detayındaki çalışan listesi. Backend'de aynı TENANT_READ_ROLES
  // kapısı duruyor (ops-tenants.ts) — dizini okuyabilen operatör içindekileri
  // de görebilsin; ayrı bir seviye tutarsız bir matris doğururdu.
  "tenants.employees.read": [
    "role_op_staff",
    "role_op_customer_success",
    "role_op_support",
    "role_op_finance",
    "role_op_sre",
    "role_op_sales",
    "role_op_exec",
    "role_op_admin",
  ],

  // Backend TENANT_PROVISION_ROLES (ops-tenants.ts) — perm_tenancy_provision,
  // spec 62. Fresh MFA is enforced server-side.
  "tenants.provision": ["role_op_admin", "role_op_sre"],

  // Justified export (spec 60 §"justified export"). Finance + Admin
  // can issue exports; Customer Success can request but Finance approves.
  "tenants.export": ["role_op_finance", "role_op_admin"],

  // ─── impersonation (plan madde 3.6, spec 60 cloud-only) ─────────
  // Spec 60 §perm_op_impersonate: customer_success + support + admin
  // (admin gets the perm via the role_op_admin row at line 1843).
  // Backend's IMPERSONATE_ROLES (services/platform-graph/src/routes/
  // ops-impersonations.ts) MUST stay synced with this list — the
  // backend wins on drift, but a missing role here hides the start
  // button from a permitted operator (degraded UX). read = start: the
  // GET /ops/v1/impersonations/active route uses the same
  // IMPERSONATE_ROLES guard (no separate read perm), so the session-
  // list screen gates on the same three roles.
  "impersonate.read": [
    "role_op_customer_success",
    "role_op_support",
    "role_op_admin",
  ],
  "impersonate.start": [
    "role_op_customer_success",
    "role_op_support",
    "role_op_admin",
  ],

  // ─── justified exports (plan madde 3.7, spec 60 §justified_export) ─
  // Backend's EXPORT_ROLES (services/platform-graph/src/routes/
  // ops-exports.ts) is [role_op_finance, role_op_admin]. read = request:
  // the route is POST-only (no GET list yet), so the screen + the
  // request form gate on the same two roles. Additionally sudo-gated
  // (requireFreshMfa, spec-60 configured freshness window; 900s default) server-side; the frontend surfaces the
  // operator.mfa_required envelope error when the sudo window lapses.
  "export.read": ["role_op_finance", "role_op_admin"],
  "export.request": ["role_op_finance", "role_op_admin"],

  // ─── billing (plan madde 3.9) ───────────────────────────────────
  // Every shipped billing route gates on role_op_admin only
  // (ops-billing.ts PLAN_OVERRIDE_READ_ROLES / PLAN_OVERRIDE_SELF_APPROVE_ROLES):
  // finance-requested overrides need the two-operator approval flow that does
  // not exist yet. The spec-60 lists (finance/exec/sales read) used to live
  // here and rendered a screen whose every request answered 403.
  "billing.read": ["role_op_admin"],
  "billing.plan_override": ["role_op_admin"],
  "billing.mark_paid": ["role_op_admin"],
  // TODO(deferred: services/platform-graph/src/services/operator-billing-approval.ts)
  // No route exists for these yet; they keep the spec-60 roles until one
  // ships and states its own gate.
  "billing.refund": ["role_op_finance", "role_op_admin"],
  "billing.credit": ["role_op_finance", "role_op_admin"],
  "billing.trial_extend": [
    "role_op_customer_success",
    "role_op_finance",
    "role_op_admin",
  ],

  // ─── platform health dashboard (plan madde 3.5) ─────────────────
  // Mirrors the backend's TENANT_READ_ROLES guard on GET /ops/v1/health
  // (services/platform-graph/src/routes/ops-health.ts) — every operator
  // role EXCEPT role_op_auditor (the auditor reads the audit sink, not
  // operational dashboards).
  "health.read": [
    "role_op_staff",
    "role_op_customer_success",
    "role_op_support",
    "role_op_finance",
    "role_op_sre",
    "role_op_sales",
    "role_op_exec",
    "role_op_admin",
  ],

  // ─── cross-cutting relay DLQ ────────────────────────────────────
  // Backend DLQ_ROLES (routes/cross-cutting-dlq.ts): the list and the
  // requeue share one guard, so read and requeue carry the same roles.
  "dlq.read": ["role_op_sre", "role_op_admin"],
  "dlq.requeue": ["role_op_sre", "role_op_admin"],

  // ─── public contact inquiries (spec 70) ─────────────────────────
  // Backend INQUIRY_ROLES (routes/support-inquiries.ts): list and the
  // forward-only transition share one guard.
  "support_inquiries.read": ["role_op_support", "role_op_sales", "role_op_admin"],
  "support_inquiries.transition": ["role_op_support", "role_op_sales", "role_op_admin"],

  // ─── incidents + SRE (plan madde 3.5) ───────────────────────────
  "incident.read": [
    "role_op_sre",
    "role_op_admin",
    "role_op_exec",
    "role_op_support",
  ],
  "incident.write": ["role_op_sre", "role_op_admin"],

  // ─── feature flags (plan madde 3.4) ─────────────────────────────
  // Kill switches escalate to admin/sre. Per-tenant overrides for
  // beta/internal flags can be CS or Finance. Catalog read mirrors the
  // backend's TENANT_READ_ROLES guard on GET /ops/v1/feature-flags
  // (services/platform-graph/src/routes/ops-feature-flags.ts) — every
  // operator role EXCEPT role_op_auditor.
  "flag.read": [
    "role_op_staff",
    "role_op_customer_success",
    "role_op_support",
    "role_op_finance",
    "role_op_sre",
    "role_op_sales",
    "role_op_exec",
    "role_op_admin",
  ],
  "flag.tenant_override": [
    "role_op_customer_success",
    "role_op_finance",
    "role_op_admin",
  ],
  "flag.kill_switch": ["role_op_sre", "role_op_admin"],

  // ─── on-prem licenses (plan madde 3.8, spec 60 + spec 63) ───────
  // Spec 63 is exact: issue/revoke authority is role_op_revenue, never the
  // catch-all admin role. Request + approval are separate frontend actions
  // so a pending maker-checker record can be visible to both operators while
  // the requester never receives an approval control. All three mutations
  // remain fresh-MFA-gated server-side.
  "licenses.read": ["role_op_revenue"],
  "licenses.issue": ["role_op_revenue"],
  "licenses.request_revoke": ["role_op_revenue"],
  "licenses.approve_revoke": ["role_op_revenue"],

  // ─── TR statutory payroll parameters (spec 60 §feat_op_statutory_
  //     parameter_management, spec 69 §entity_payroll_parameter_set) ──
  // Backend's PAYROLL_OPS_ROLES (services/platform-graph/src/routes/
  // ops-payroll-parameter-sets.ts) is [role_op_payroll_ops] EXACTLY —
  // role_op_admin is deliberately not an alternate, the same separation
  // spec 63 gives license revocation. `publish` is the DRAFT action (the
  // screen's "new version" form): drafting is what an operator starts,
  // and nothing it writes governs a paycheck until `approve` — performed
  // by a DIFFERENT payroll-ops operator with fresh MFA — publishes it.
  // `reject` is four-eyes for the same reason: the backend refuses the
  // drafter's own draft, and the screen hides both controls rather than
  // offering a button that always 403s.
  "payroll_parameters.read": ["role_op_payroll_ops"],
  "payroll_parameters.publish": ["role_op_payroll_ops"],
  "payroll_parameters.approve": ["role_op_payroll_ops"],
  "payroll_parameters.reject": ["role_op_payroll_ops"],

  // ─── operator audit sink (plan madde 3.3) ───────────────────────
  // Auditor role's defining capability — read EVERYTHING in the
  // operator audit sink, no tenant data surface. Backend's
  // AUDIT_READ_ROLES (services/platform-graph/src/routes/ops-audit.ts)
  // is exactly [exec, admin, auditor] per spec 60 line 1842-1844
  // (perm_op_audit_read). role_op_sre is NOT included — SREs use the
  // health dashboard / incident surfaces, not the audit log.
  "audit.read": ["role_op_auditor", "role_op_admin", "role_op_exec"],
  "audit.export_for_soc2": ["role_op_auditor"],

  // ─── internal IAM (spec 60 §feat_op_internal_iam) ───────────────
  "iam.manage": ["role_op_admin"],
  "iam.invite_operator": ["role_op_admin"],

  // ─── sales / CRM lite ───────────────────────────────────────────
  "sales.note": ["role_op_sales", "role_op_customer_success"],

  // ─── operator's own session (always-allowed; explicit so
  //     usePermission works for nav/profile flows uniformly) ──────
  "self.read_own_audit": OPERATOR_ROLES, // every operator
  "self.profile": OPERATOR_ROLES,
} as const satisfies Record<string, readonly OperatorRole[]>;

export type OperatorAction = keyof typeof OPERATOR_ACTIONS;

/**
 * Pure helper — no React, no async. Returns true iff the caller's role
 * list contains AT LEAST ONE role allowed for the action.
 *
 * Defensive on input: an unknown action ID returns false (caller mis-spell
 * → no UX leak; backend would 403 anyway). An empty roles list returns
 * false except for actions whose allow-list explicitly includes a role
 * from an empty list (currently none).
 */
export function isPermitted(
  action: OperatorAction,
  roles: readonly string[],
): boolean {
  const allowed = OPERATOR_ACTIONS[action];
  if (allowed === undefined) return false;
  return allowed.some((r) => roles.includes(r));
}

/**
 * Pure helper — true iff the role list contains the specific role.
 * Used by RequireRole for "can_only_be_done_by_one_role" gates that
 * don't merit an action ID (e.g., showing the IAM admin link in the
 * left rail only to role_op_admin).
 */
export function hasRole(
  role: OperatorRole,
  roles: readonly string[],
): boolean {
  return roles.includes(role);
}

/**
 * True iff the role list contains AT LEAST ONE of the listed roles.
 * Convenience for UI elements gated on "any of {sre, admin}" without
 * needing to invent an action ID for the union.
 */
export function hasAnyRole(
  required: readonly OperatorRole[],
  roles: readonly string[],
): boolean {
  return required.some((r) => roles.includes(r));
}
